import React, { useEffect, useRef, useState, SyntheticEvent } from "react";
import { Box, Typography, TextField, Button, Paper } from "@mui/material";
import { LoadingButton, MoneyField, Notice } from "@routes-plan/ui";
import { companyCurrency, companyLocale } from "../../../../utils/format/currency";
import PhotoUploadButton from "../../../../components/attachments/PhotoUploadButton";
import { newRequestId } from "../../../../components/attachments/photoUpload";
import { dailyLimitMessage } from "../../../../utils/dailyLimit";
import StandardModal, { useModalLoading } from "../../../../components/modales/StandardModal";
import { ExpenseType } from "../../../../models/expense-type";
import axios from "axios";
import moment from "moment-timezone";
import { operationalTimezone } from '../../../../utils/operationalDateTime';
import { useT } from '../../../../i18n';

interface MyModalProps {
  isOpen: boolean;
  onClose: () => void;
  routeId: string;
  cashBoxId: number;
  userId: string;
  selectedDate: string;
  showLoading: () => void;
  hideLoading: () => void;
  canSave: boolean;
  onAfterSubmit: (createdExpenses?: {
    id: number;
    expenseTypeId: number;
    value: number;
    description: string;
    name: string;
    cashBoxId: number;
  }[]) => Promise<void> | void;
}

const RouteExpensesModal: React.FC<MyModalProps> = ({
  isOpen,
  onClose,
  routeId,
  cashBoxId,
  userId,
  selectedDate,
  canSave,
  onAfterSubmit,
}) => {
  // Cargando propio del modal: lista de gastos y guardado (el general queda detrás).
  const { loading, showLoading, hideLoading } = useModalLoading();
  const t = useT();

  const [expenses, setExpenses] = useState<ExpenseType[]>([]);
  const [values, setValues] = useState<{ [key: number]: number }>({});
  const [descriptions, setDescriptions] = useState<{ [key: number]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  // Identificador de cada gasto (une su foto subida antes) y reglas del cobro.
  const requestIds = useRef<{ [key: number]: string }>({});
  const [photos, setPhotos] = useState<{ [key: number]: boolean }>({});
  const [requirePhoto, setRequirePhoto] = useState(false);
  const [error, setError] = useState('');
  const requestIdFor = (expenseId: number) => {
    if (!requestIds.current[expenseId]) requestIds.current[expenseId] = newRequestId('expense');
    return requestIds.current[expenseId];
  };


  useEffect(() => {
    if (isOpen) {
      setValues({});
      setDescriptions({});
      setIsSaving(false);
      setPhotos({});
      setError('');
      requestIds.current = {};
      axios
        .get(`routes/${routeId}`)
        .then(({ data }) => setRequirePhoto(Boolean(Number(data?.data?.[0]?.requireExpensePhoto ?? 0))))
        .catch(() => setRequirePhoto(false));

      const fetchData = async () => {
        showLoading();
        try {
          const { data } = await axios.post('expense-types/search', { categoryId: 1, operationTypeId: 2 });
          setExpenses(data.data);
        } catch (error) {
          console.error('Error fetching expenses data:', error);
        } finally {
          hideLoading();
        }
      };

      fetchData();
    }
  }, [isOpen, showLoading, hideLoading, routeId]);

  const handleValueChange = (expenseId: number, value: number) => {
    setValues(prev => ({ ...prev, [expenseId]: value }));
  };

  const handleDescriptionChange = (expenseId: number, description: string) => {
    setDescriptions(prev => ({ ...prev, [expenseId]: description }));
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (savingRef.current) return;
    if (!canSave) return;

    const expenseDate = moment
      .tz(selectedDate, 'YYYY-MM-DD', operationalTimezone())
      .format('YYYY-MM-DD');

    const formData = expenses
      .filter(e => values[e.id] > 0)
      .map(e => ({
        description: descriptions[e.id] || "",
        value: values[e.id],
        expenseTypeId: e.id,
        userId,
        routeId: Number(routeId),
        originCashBoxId: cashBoxId,
        date: expenseDate,
        operationTypeId: 2,
        clientRequestId: requestIdFor(e.id),
      }));

    if (formData.length === 0) {
      return;
    }

    try {
      savingRef.current = true;
      setIsSaving(true);
      showLoading();
      const { data } = await axios.post("/cash-box-expenses", { expenses: formData });
      const createdExpenses = (data?.data || []).map((item: any) => {
        const expense = expenses.find(e => Number(e.id) === Number(item.expenseType?.id || item.expenseTypeId));
        return {
          id: Number(item.id),
          expenseTypeId: Number(item.expenseType?.id || item.expenseTypeId),
          value: Number(item.value || 0),
          description: item.description || "",
          name: expense?.name || "",
          cashBoxId,
        };
      });
      savingRef.current = false;
      setIsSaving(false);
      onClose();
      Promise.resolve(onAfterSubmit(createdExpenses)).catch((refreshError) => {
        console.error('Error refreshing expenses after save:', refreshError);
      });
    } catch (error: any) {
      console.error('Error:', error);
      const code = error?.response?.data?.code;
      setError(
        code === 'PHOTO_REQUIRED'
          ? t('attachments.photoRequiredMessage')
          : dailyLimitMessage(error, t) ?? error?.response?.data?.message ?? t('common.saveError'),
      );
      savingRef.current = false;
      setIsSaving(false);
    } finally {
      hideLoading();
    }
  };

  return (
    <StandardModal
      open={isOpen}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      title={t('routeSettlement.registerExpenses')}
      maxWidth={600}
      loading={loading}
      disableClose={isSaving}
      actions={
        <>
          <Button variant="outlined" color="inherit" onClick={() => {
            if (!isSaving) onClose();
          }} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={submit}
            loading={isSaving}
            disabled={expenses.length === 0 || !canSave}
          >
            {t('common.save')}
          </LoadingButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('routeSettlement.recordDate', { date: moment.tz(selectedDate, 'YYYY-MM-DD', operationalTimezone()).format('DD/MM/YYYY') })}
      </Typography>
      {requirePhoto && <Box sx={{ mb: 2 }}><Notice severity="info">{t('attachments.photoRequiredHint')}</Notice></Box>}
      {error && <Box sx={{ mb: 2 }}><Notice severity="warning" dismissible onClose={() => setError('')}>{error}</Notice></Box>}

      {expenses.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('routeSettlement.noExpenseTypes')}</Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {expenses.map(expense => (
            <Paper key={expense.id} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{expense.name}</Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                <MoneyField
                  id={`expense-value-${expense.id}`}
                  fullWidth
                  label={t('routeSettlement.value')}
                  value={values[expense.id] || null}
                  onChange={(v) => handleValueChange(expense.id, Number(v ?? 0))}
                  currency={companyCurrency()}
                  locale={companyLocale()}
                />
                <TextField
                  id={`expense-description-${expense.id}`}
                  fullWidth
                  label={t('routeSettlement.description')}
                  value={descriptions[expense.id] || ""}
                  onChange={(e) => handleDescriptionChange(expense.id, e.target.value)}
                />
                {values[expense.id] > 0 && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <PhotoUploadButton
                      ownerType="expense"
                      ownerRef={requestIdFor(expense.id)}
                      routeId={Number(routeId)}
                      required={requirePhoto && !photos[expense.id]}
                      onUploaded={() => setPhotos((p) => ({ ...p, [expense.id]: true }))}
                      onError={setError}
                      dataTour="expense-photo"
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </StandardModal>
  );
};

export default RouteExpensesModal;
